// frontend/src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import NotificationBar from './NotificationBar';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Collapse } from
'@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CloudQueue as CloudIcon,
  Security as SecurityIcon,
  IntegrationInstructions as IntegrationIcon,
  Help as HelpIcon,
  Code as CodeIcon,
  Videocam as VideocamIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  SystemUpdate as FirmwareIcon,
  LocalHospital as AlarmIcon,
  Map as MapIcon,
  Apps as AppsIcon,
  Devices as DevicesIcon,
  Monitor as MonitorIcon,
  Link as LinkIcon,
  Router as RouterIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
  RadioButtonChecked as ButtonIcon,
  PowerSettingsNew as PowerIcon,
  ExpandLess,
  ExpandMore,
  AccountTree as AccountTreeIcon,
  SmartToy as AutomationIcon,
  AutoFixHigh as BulkNameIcon,
  Phone as PhoneIcon } from
'@mui/icons-material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useThemeSettings } from "../contexts/ThemeContext";
import SiteFilterSelector from './SiteFilterSelector';
import axios from 'axios';

const drawerWidth = 240;

const Layout = () => {const { t } = useThemeSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { canManageUsers, hasPermission } = usePermissions();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);

  // Load company settings on mount
  useEffect(() => {
    const loadCompanySettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
        const token = localStorage.getItem('auth_token');
        if (token) {
          const response = await axios.get(`${API_URL}/api/company/settings`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
            setCompanySettings(response.data.data.settings);
          }
        }
      } catch (error) {
        console.error('Error loading company settings:', error);
        // Fail silently - settings will be null
      }
    };

    if (user) {
      loadCompanySettings();
    }
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Debug menu visibility
  const cameraRequestsPermission = hasPermission('camera_requests.view');
  // Camera requests should show if user has permission, regardless of company setting
  // Company setting controls feature enablement, but menu should be visible for users with permission
  const cameraSettingEnabled = companySettings?.cameraFootageRequestsEnabled !== false;
  const fieldInterventionsPermission = hasPermission('field_interventions.view');
  const companyManagementPermission = hasPermission('company.settings.manage') || hasPermission('company.edit') || user?.role === 'admin';

  //console.log('[Layout] Menu visibility:', {
  //  cameraRequestsPermission,
  //  cameraSettingEnabled,
  //  fieldInterventionsPermission,
  //  companySettings,
  //  companyManagementPermission,
  //  userRole: user?.role
  //});

  const menuItems = [
  { text: t ? t('Layout.menu_dashboard') : 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: t ? t('Layout.menu_locations') : 'Locations', icon: <LocationOnIcon />, path: '/locations' },
  { text: t ? t('Layout.menu_devices') : 'Devices', icon: <CloudIcon />, path: '/devices' },
  { text: t ? t('Layout.menu_persons') : 'Persons', icon: <PeopleIcon />, path: '/persons' },
  { text: t ? t('Layout.menu_tickets') : 'Tickets', icon: <BugReportIcon />, path: '/tickets' },
  { text: t ? t('Layout.menu_alarms') : 'Alarms', icon: <AlarmIcon />, path: '/alarms', adminOnly: true },
  { text: t ? t('Layout.menu_notifications') : 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
  { text: t ? t('Layout.menu_camera_requests') : 'Camera Requests', icon: <VideocamIcon />, path: '/camera-requests', requiresPermission: cameraRequestsPermission },
  { text: t ? t('Layout.menu_field_interventions') : 'Field Interventions', icon: <EngineeringIcon />, path: '/field-interventions', requiresPermission: fieldInterventionsPermission },
  { text: t ? t('Layout.menu_user_management') : 'User management', icon: <SecurityIcon />, path: '/users', requiresPermission: canManageUsers }];

  // Grouped bottom menu items
  const bottomMenuGroups = [
    {
      id: 'management',
      label: t ? t('Layout.group_management') : 'Management',
      icon: <BusinessIcon />,
      adminOnly: false,
      items: [
        { text: t ? t('Layout.menu_send_notifications') : 'Send Notifications', icon: <NotificationsActiveIcon />, path: '/notification-management', requiresPermission: canManageUsers },
        { text: t ? t('Layout.menu_company_management') : 'Company Management', icon: <BusinessIcon />, path: '/company-management', requiresPermission: companyManagementPermission },
        { text: t ? t('Layout.menu_person_type_management') : 'Person Type Management', icon: <CategoryIcon />, path: '/person-type-management', requiresPermission: canManageUsers },
      ],
    },
    {
      id: 'device-management',
      label: t ? t('Layout.group_device_management') : 'Device Management',
      icon: <DevicesIcon />,
      adminOnly: true,
      items: [
        { text: t ? t('Layout.menu_device_type_management') : 'Device Type Management', icon: <DevicesIcon />, path: '/device-type-management', adminOnly: true },
        { text: t ? t('Layout.menu_device_linking') : 'Device Linking', icon: <LinkIcon />, path: '/device-linking', adminOnly: true },
        { text: t ? t('Layout.menu_firmware_management') : 'Firmware Management', icon: <FirmwareIcon />, path: '/firmware-management', adminOnly: true },
      ],
    },
    {
      id: 'integrations',
      label: t ? t('Layout.group_integrations') : 'Integrations',
      icon: <AccountTreeIcon />,
      adminOnly: true,
      items: [
        { text: t ? t('Layout.menu_enocean_buttons') : 'EnOcean Buttons', icon: <ButtonIcon />, path: '/enocean-buttons', adminOnly: true },
        { text: t ? t('Layout.menu_qbus_output_types') : 'Qbus Output Types', icon: <PowerIcon />, path: '/qbus-output-types', adminOnly: true },
        { text: t ? t('Layout.menu_qbus_automations') : 'Qbus Automations', icon: <AutomationIcon />, path: '/qbus-automations', adminOnly: true },
        { text: t ? t('Layout.menu_qbus_friendly_names') : 'Qbus Friendly Names', icon: <BulkNameIcon />, path: '/qbus-friendly-names', adminOnly: true },
        { text: t ? t('Layout.menu_baseip_management') : 'BaseIP Management', icon: <RouterIcon />, path: '/baseip-management', adminOnly: true },
        { text: t ? t('Layout.menu_indoor_mapping') : 'Indoor Mapping', icon: <MapIcon />, path: '/indoor-mapping', adminOnly: true },
        { text: t ? t('Layout.menu_telephony') : 'Telephony', icon: <PhoneIcon />, path: '/telephony', adminOnly: true },
        { text: t ? t('Layout.menu_integration_management') : 'Integration Management', icon: <IntegrationIcon />, path: '/integration-management', adminOnly: true },
      ],
    },
    {
      id: 'system',
      label: t ? t('Layout.group_system') : 'System',
      icon: <SettingsIcon />,
      adminOnly: true,
      items: [
        { text: t ? t('Layout.menu_service_management') : 'Service Management', icon: <AppsIcon />, path: '/service-management', adminOnly: true },
        { text: t ? t('Layout.menu_monitoring') : 'Monitoring', icon: <MonitorIcon />, path: '/monitoring', adminOnly: true },
        { text: t ? t('Layout.menu_developer_info') : 'Developer Info', icon: <CodeIcon />, path: '/developer-info', adminOnly: true },
      ],
    },
  ];

  // Standalone bottom items (not in any group)
  const bottomStandaloneItems = [
    { text: t ? t('Layout.menu_help') : 'Help', icon: <HelpIcon />, path: '/help' },
  ];

  // Collapse state with localStorage persistence
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('menuOpenGroups')) || {}; }
    catch { return {}; }
  });

  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('menuOpenGroups', JSON.stringify(next));
      return next;
    });
  };

  // Auto-expand group containing the active route
  useEffect(() => {
    for (const group of bottomMenuGroups) {
      if (group.items.some(item => location.pathname.startsWith(item.path))) {
        if (!openGroups[group.id]) {
          setOpenGroups(prev => {
            const next = { ...prev, [group.id]: true };
            localStorage.setItem('menuOpenGroups', JSON.stringify(next));
            return next;
          });
        }
        break;
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter helper
  const isItemVisible = (item) => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.requiresPermission !== undefined && !item.requiresPermission) return false;
    return true;
  };


  const drawer =
  <div>
       <Toolbar sx={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
          src="/logo/logo-full.png"
          alt={t ? t("Layout.caro_logo") : "CARO Logo"}
          style={{ height: 50 }} />
        
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.
      filter(isItemVisible).
      map((item) =>
      <ListItem key={item.text + item.path} disablePadding>
            <ListItemButton
          selected={location.pathname.startsWith(item.path)}
          onClick={() => {
            navigate(item.path);
            setMobileOpen(false);
          }}>

              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  color={item.badgeColor || 'default'}
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </ListItemButton>
          </ListItem>
      )}
      </List>
      <Divider />
      <List component="nav" dense>
        {bottomMenuGroups.map((group) => {
          // Filter visible items in this group
          const visibleItems = group.items.filter(isItemVisible);
          // Hide entire group if no visible items
          if (visibleItems.length === 0) return null;
          // Hide group if group itself is admin-only and user is not admin
          if (group.adminOnly && user?.role !== 'admin') return null;

          const isOpen = openGroups[group.id] || false;

          return (
            <React.Fragment key={group.id}>
              <ListItemButton onClick={() => toggleGroup(group.id)} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {group.icon}
                </ListItemIcon>
                <ListItemText
                  primary={group.label}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {visibleItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      selected={location.pathname.startsWith(item.path)}
                      sx={{ pl: 4, py: 0.25 }}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ variant: 'body2', fontSize: '0.82rem' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}

        {/* Standalone items (Help) */}
        {bottomStandaloneItems.filter(isItemVisible).map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname.startsWith(item.path)}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>;


  return (
    <>
    {/* Skip to content link for keyboard navigation */}
    <a href="#main-content" className="skip-to-content">{t ? t("Layout.skip_content") : "Skip to content"}

      </a>

    <Box sx={{ display: 'flex' }}>
      <AppBar
          position="fixed"
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` }
          }}>
          
        <Toolbar>
          <IconButton
              color="inherit"
              aria-label={t ? t("Layout.open_drawer") : "open drawer"}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}>
              
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => location.pathname.startsWith(item.path))?.text
              || bottomMenuGroups.flatMap(g => g.items).find((item) => location.pathname.startsWith(item.path))?.text
              || bottomStandaloneItems.find((item) => location.pathname.startsWith(item.path))?.text
              || 'Caro Management Controller'}
          </Typography>

          {/* Site Filter */}
          <SiteFilterSelector />

          {/* Notification Center */}
          <NotificationCenter />

          <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ p: 0 }}>

            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.username?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          
          <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              
            <MenuItem disabled>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {user?.username || 'User'}
                <Chip
                    label={user?.role || 'User'}
                    size="small"
                    sx={{ ml: 1 }} />
                  
              </ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => navigate('/settings')}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t ? t("Layout.settings") : "Settings"}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t ? t("Layout.logout") : "Logout"}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
          
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
            }}>
            
          {drawer}
        </Drawer>
        <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
            }}
            open>
            
          {drawer}
        </Drawer>
      </Box>

      <Box
          component="main"
          id="main-content"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
            maxWidth: { xs: '100vw', md: `calc(100vw - ${drawerWidth}px)` },
            minHeight: '100vh',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
          
        <Toolbar />

        {/* System Notification Bar */}
        <NotificationBar />

        <Box
            sx={{
              flexGrow: 1,
              maxWidth: '100%',
              width: '100%',
              overflowX: 'hidden',
              boxSizing: 'border-box'
            }}>

          <Outlet />
        </Box>
        
        {/* Footer with version */}
        <Box
            component="footer"
            sx={{
              textAlign: 'center',
              py: 2,
              mt: 'auto',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
            
          <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                opacity: 0.6
              }}>{t ? t("Layout.caro_management_controller_v251006b") : "CARO MANAGEMENT CONTROLLER v26.02.17betà"}


            </Typography>
        </Box>
      </Box>
    </Box>
    </>);

};

export default Layout;